/**
 * IEPReportPage
 * IEP-specific goals report and export
 * BP-06-03: Care Team & Goals Frontend
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  FileText,
  Printer,
  Calendar,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { useIEPReport } from '@/features/goals/hooks/useGoals';
import { IEPGoalsSummary, GoalProgressChart } from '../components';
import { goalsApi } from '@/features/goals/api/goalsApi';
import type { Goal } from '@/features/goals/types/goalsTypes';

export function IEPReportPage() {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState(false);

  const { data: report, isLoading, error } = useIEPReport(childId!);

  const handleExport = async (format: 'pdf' | 'csv') => {
    setIsExporting(true);
    try {
      const blob = await goalsApi.exportGoals(childId!, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `iep-goals-report-${new Date().toISOString().split('T')[0]}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleGoalClick = (goal: Goal) => {
    navigate(`/children/${childId}/goals/${goal.id}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-sage" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-terracotta" />
        <h2 className="text-xl font-semibold text-navy mb-2">Error Loading Report</h2>
        <p className="text-warm-gray mb-4">Unable to generate IEP report.</p>
        <button
          onClick={() => navigate(`/children/${childId}/goals`)}
          className="text-sage hover:text-sage-dark"
        >
          ← Back to Goals
        </button>
      </div>
    );
  }

  const goals = report?.goals || [];
  const iepGoals = goals.filter((g) => g.isIepGoal);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate(`/children/${childId}/goals`)}
        className="inline-flex items-center gap-2 text-warm-gray hover:text-navy mb-6 transition-colors print:hidden"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Goals
      </button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 print:mb-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold text-navy flex items-center gap-3">
            <FileText className="w-8 h-8 text-sage" />
            IEP Goals Report
          </h1>
          <p className="text-warm-gray mt-1 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Generated: {new Date().toLocaleDateString()}
          </p>
        </div>

        <div className="flex items-center gap-2 print:hidden">
          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-2 px-4 py-2 text-navy hover:bg-cream rounded-lg transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={isExporting}
            className="inline-flex items-center gap-2 px-4 py-2 border-2 border-sage text-sage hover:bg-sage/10 rounded-lg transition-colors disabled:opacity-50"
          >
            {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Export PDF
          </button>
        </div>
      </div>

      {/* Report Content */}
      <div className="space-y-8">
        {/* Summary Section */}
        <section className="bg-white rounded-xl border border-warm-gray/10 p-6 print:shadow-none print:border">
          <h2 className="text-lg font-display font-semibold text-navy mb-4">Goals Summary</h2>
          <IEPGoalsSummary
            goals={goals}
            onGoalClick={handleGoalClick}
          />
        </section>

        {/* Progress Charts Section */}
        {iepGoals.length > 0 && (
          <section className="bg-white rounded-xl border border-warm-gray/10 p-6 print:break-before-page print:shadow-none print:border">
            <h2 className="text-lg font-display font-semibold text-navy mb-6">Progress Charts</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {iepGoals.slice(0, 6).map((goal) => (
                <div key={goal.id} className="border border-warm-gray/10 rounded-lg p-4">
                  <h3 className="font-medium text-navy mb-3 truncate">{goal.title}</h3>
                  <div className="h-40">
                    <GoalProgressChart
                      entries={[]} // Would need progress data per goal
                      measurementType={goal.measurementType}
                      targetValue={goal.targetValue}
                      compact
                    />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="text-warm-gray">{goal.iepArea}</span>
                    <span className="font-medium text-navy">{goal.progressPercentage}%</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Detailed Goals List */}
        <section className="bg-white rounded-xl border border-warm-gray/10 p-6 print:break-before-page print:shadow-none print:border">
          <h2 className="text-lg font-display font-semibold text-navy mb-6">Detailed Goals</h2>
          
          {iepGoals.length === 0 ? (
            <div className="text-center py-8 text-warm-gray">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No IEP goals to display</p>
            </div>
          ) : (
            <div className="space-y-6">
              {iepGoals.map((goal, index) => (
                <div
                  key={goal.id}
                  className="border-b border-warm-gray/10 pb-6 last:border-0 last:pb-0 print:break-inside-avoid"
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <span className="text-sm text-warm-gray">Goal {index + 1}</span>
                      <h3 className="text-lg font-medium text-navy">{goal.title}</h3>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      goal.status === 'achieved' ? 'bg-green-100 text-green-700' :
                      goal.status === 'in_progress' ? 'bg-sage/10 text-sage-dark' :
                      goal.status === 'discontinued' ? 'bg-terracotta/10 text-terracotta' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {goal.status.replace('_', ' ')}
                    </span>
                  </div>

                  {goal.description && (
                    <p className="text-navy-light mb-3">{goal.description}</p>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <dt className="text-warm-gray">IEP Area</dt>
                      <dd className="font-medium text-navy">{goal.iepArea || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-warm-gray">Category</dt>
                      <dd className="font-medium text-navy">{goal.category || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-warm-gray">Target Date</dt>
                      <dd className="font-medium text-navy">
                        {goal.targetDate ? new Date(goal.targetDate).toLocaleDateString() : 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-warm-gray">Progress</dt>
                      <dd className="font-medium text-navy">{goal.progressPercentage}%</dd>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-3">
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          goal.progressPercentage >= 100 ? 'bg-green-500' :
                          goal.progressPercentage >= 50 ? 'bg-sage' : 'bg-terracotta'
                        }`}
                        style={{ width: `${Math.min(100, goal.progressPercentage)}%` }}
                      />
                    </div>
                  </div>

                  {goal.notes && (
                    <div className="mt-3 p-3 bg-cream/50 rounded-lg">
                      <dt className="text-xs text-warm-gray uppercase tracking-wide mb-1">Notes</dt>
                      <dd className="text-sm text-navy-light">{goal.notes}</dd>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Footer */}
        <footer className="text-center text-sm text-warm-gray print:mt-8">
          <p>Report generated by BrightPath Care Management Platform</p>
          <p>For questions, contact your care coordinator</p>
        </footer>
      </div>
    </div>
  );
}

export default IEPReportPage;


